import { memo, useCallback } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button, Tooltip } from '@aimsos/ui';

import { NBA_ACTION_TYPES, NBA_CONCIERGE_INTENT_BY_ACTION } from '../../constants/nextBestAction.js';
import { useConciergeHandoff } from '../../hooks/useConciergeHandoff.js';

/**
 * One action of a Next Best Action — the card's primary action, a "way to do
 * it", an "other recommendation" (CT-5713). The label comes from the action
 * TYPE; a draft someone else is reviewing reads "View draft (read-only)".
 *
 * An action that goes to the Concierge hands off HERE, through
 * `useConciergeHandoff` (CT-5721): the hand-off follows the shell chat, which
 * changes with every streamed message, so only this button re-renders with it —
 * never the profile page that lists it. While the shell cannot open the
 * Concierge (`available`) the button is disabled with a tooltip saying why: the
 * flow is never faked. Every other action (the slide-out modes) goes to
 * `onAction(item, action)`, which the page owns.
 *
 * @param {object} props
 * @param {{ type: string, id: string, title: string }} props.entity — the record
 *   the Concierge is about.
 * @param {(item: object, action: object) => void} props.onAction
 */
const NextBestActionActionButton = memo(function NextBestActionActionButton({
  item,
  action,
  variant = 'primary',
  entity,
  onAction,
}) {
  const { t } = useTranslation('unified-profiles');
  const { available, openConcierge } = useConciergeHandoff();
  const intent = action?.type ? NBA_CONCIERGE_INTENT_BY_ACTION[action.type] : undefined;

  const handleClick = useCallback(() => {
    if (!intent) {
      onAction(item, action);
      return;
    }
    openConcierge({
      intent,
      entity,
      itemId: item.id,
      draftId: action.draftId ?? null,
      readOnly: Boolean(item.reviewer),
    });
  }, [intent, onAction, openConcierge, entity, item, action]);

  if (!action?.type) return null;

  const readOnly = action.type === NBA_ACTION_TYPES.REVIEW_DRAFT && Boolean(item.reviewer);
  const label = readOnly ? t('profile.nba.actions.view_draft_read_only') : t(`profile.nba.actions.${action.type}`);
  const blocked = Boolean(intent) && !available;

  const button = (
    <Button variant={variant} size="sm" onClick={handleClick} disabled={blocked}>
      {label}
    </Button>
  );

  if (!blocked) return button;

  return (
    <Tooltip content={t('profile.nba.conciergeUnavailable')} size="compact">
      {button}
    </Tooltip>
  );
});

export default NextBestActionActionButton;
